"use client";

import { c as _c } from "react/compiler-runtime";
import MediaTypeBadge from "@/components/drops/media/MediaTypeBadge";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import MemeDropTraits from "@/components/memes/drops/MemeDropTraits";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import CommonDropdownItemsMobileWrapper from "@/components/utils/select/dropdown/CommonDropdownItemsMobileWrapper";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import WaveDropActionsOpen from "@/components/waves/drops/WaveDropActionsOpen";
import WaveDropMobileMenuCopyLink from "@/components/waves/drops/WaveDropMobileMenuCopyLink";
import WaveDropMobileMenuOpen from "@/components/waves/drops/WaveDropMobileMenuOpen";
import WaveDropTime from "@/components/waves/drops/time/WaveDropTime";
import { DropAuthorBadges } from "@/components/waves/drops/DropAuthorBadges";
import { getRankHoverBorderClass } from "@/components/waves/drops/dropRankStyles";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ApiWaveDecisionWinner } from "@/generated/models/ApiWaveDecisionWinner";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { convertApiDropToExtendedDrop } from "@/helpers/waves/drop.helpers";
import { WAVE_VOTE_STATS_LABELS, WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { TOOLTIP_STYLES } from "@/helpers/tooltip.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import useLongPressInteraction from "@/hooks/useLongPressInteraction";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "react-tooltip";
import WaveWinnersDropHeaderAuthorPfp from "./header/WaveWinnersDropHeaderAuthorPfp";
import { WaveWinnerIdentity } from "../identity/WaveWinnerIdentity";
interface MemesWaveWinnersDropProps {
  readonly winner: ApiWaveDecisionWinner;
  readonly wave: ApiWave;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
const getRankHoverClass = (place: number | null): string => {
  return getRankHoverBorderClass(place);
};
const isClickFromCardDom = (event: React.MouseEvent<HTMLDivElement>): boolean => {
  return event.currentTarget.contains(event.target as Node);
};
export const MemesWaveWinnersDrop: React.FC<MemesWaveWinnersDropProps> = t0 => {
  const $ = _c(160);
  const {
    winner,
    wave,
    onDropClick
  } = t0;
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const suppressNextClickRef = React.useRef(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      suppressNextClickRef.current = true;
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleInteractionStart = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = event => {
      if (!isClickFromCardDom(event)) {
        return;
      }
      if (!suppressNextClickRef.current) {
        return;
      }
      suppressNextClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleClickCapture = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      suppressNextClickRef.current = false;
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleMenuClickCapture = t3;
  let t4;
  if ($[3] !== hasTouchScreen) {
    t4 = {
      hasTouchScreen,
      onInteractionStart: handleInteractionStart,
      preventDefault: false
    };
    $[3] = hasTouchScreen;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    isActive,
    setIsActive,
    touchHandlers
  } = useLongPressInteraction(t4);
  let t5;
  if ($[5] !== setIsActive) {
    t5 = nextIsActive => {
      if (!nextIsActive) {
        suppressNextClickRef.current = false;
      }
      setIsActive(nextIsActive);
    };
    $[5] = setIsActive;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleMobileMenuOpenChange = t5;
  let t6;
  if ($[7] !== handleMobileMenuOpenChange) {
    t6 = () => {
      handleMobileMenuOpenChange(false);
    };
    $[7] = handleMobileMenuOpenChange;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const handleMobileMenuClose = t6;
  const title = winner.drop.metadata.find(_temp)?.data_value ?? "Artwork Title";
  const description = winner.drop.metadata.find(_temp2)?.data_value ?? "This is an artwork submission for The Memes collection.";
  let t7;
  if ($[9] !== winner.drop.parts) {
    t7 = winner.drop.parts.at(0)?.media.at(0);
    $[9] = winner.drop.parts;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const artworkMedia = t7;
  const rating = winner.drop.rating || 0;
  const ratersCount = winner.drop.raters_count;
  let creditType;
  let extendedDrop;
  let hasUserVoted;
  let isUserVoteNegative;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t8;
  let t9;
  let userVote;
  let userVoteClass;
  if ($[11] !== artworkMedia || $[12] !== description || $[13] !== hasTouchScreen || $[14] !== onDropClick || $[15] !== rating || $[16] !== title || $[17] !== touchHandlers || $[18] !== wave.voting.credit_type || $[19] !== winner) {
    const topVoters = winner.drop.top_raters.slice(0, 3);
    creditType = WAVE_VOTING_LABELS[wave.voting.credit_type];
    hasUserVoted = winner.drop.context_profile_context?.rating !== undefined && winner.drop.context_profile_context.rating !== 0;
    userVote = winner.drop.context_profile_context?.rating ?? 0;
    isUserVoteNegative = userVote < 0;
    const totalVoteClass = rating < 0 ? "tw-text-rose-400" : "tw-text-iron-50";
    userVoteClass = isUserVoteNegative ? "tw-text-rose-400" : "tw-text-iron-50";
    let t25;
    if ($[43] !== winner.drop) {
      t25 = convertApiDropToExtendedDrop(winner.drop);
      $[43] = winner.drop;
      $[44] = t25;
    } else {
      t25 = $[44];
    }
    extendedDrop = t25;
    let t26;
    if ($[45] !== extendedDrop || $[46] !== onDropClick) {
      t26 = event_0 => {
        if (!isClickFromCardDom(event_0)) {
          return;
        }
        onDropClick(extendedDrop);
      };
      $[45] = extendedDrop;
      $[46] = onDropClick;
      $[47] = t26;
    } else {
      t26 = $[47];
    }
    const handleClick = t26;
    t22 = handleClickCapture;
    t23 = handleClick;
    t24 = "touch-select-none tw-w-full tw-cursor-pointer tw-rounded-xl tw-transition-all tw-duration-300 tw-ease-out";
    let t27;
    if ($[48] !== winner.place) {
      t27 = getRankHoverClass(winner.place);
      $[48] = winner.place;
      $[49] = t27;
    } else {
      t27 = $[49];
    }
    t21 = `tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950 tw-transition-all tw-duration-200 tw-ease-out ${t27}`;
    t15 = "tw-flex tw-flex-col";
    t16 = touchHandlers;
    let t28;
    if ($[50] !== winner) {
      t28 = <WaveWinnersDropHeaderAuthorPfp winner={winner} size="sm" />;
      $[50] = winner;
      $[51] = t28;
    } else {
      t28 = $[51];
    }
    let t29;
    if ($[52] !== winner.drop.author.handle || $[53] !== winner.drop.author.id || $[54] !== winner.drop.author.primary_address) {
      t29 = winner.drop.author.handle ? <UserProfileTooltipWrapper user={winner.drop.author.handle}><Link href={`/${winner.drop.author.handle}`} onClick={_temp3} scroll={false} className="tw-no-underline desktop-hover:hover:tw-underline"><span className="tw-text-sm tw-font-bold tw-text-white">{winner.drop.author.handle}</span></Link></UserProfileTooltipWrapper> : <Link href={`/${winner.drop.author.handle ?? winner.drop.author.primary_address}`} onClick={_temp4} scroll={false} className="tw-no-underline desktop-hover:hover:tw-underline"><span className="tw-text-sm tw-font-bold tw-text-white">{winner.drop.author.handle ?? winner.drop.author.id}</span></Link>;
      $[52] = winner.drop.author.handle;
      $[53] = winner.drop.author.id;
      $[54] = winner.drop.author.primary_address;
      $[55] = t29;
    } else {
      t29 = $[55];
    }
    let t30;
    if ($[56] !== winner.drop.author.level) {
      t30 = <UserCICAndLevel level={winner.drop.author.level} size={UserCICAndLevelSize.SMALL} />;
      $[56] = winner.drop.author.level;
      $[57] = t30;
    } else {
      t30 = $[57];
    }
    const t31 = `memes-winner-author-badges-${winner.drop.id}`;
    let t32;
    if ($[58] !== t31 || $[59] !== winner.drop.author) {
      t32 = <DropAuthorBadges profile={winner.drop.author} tooltipIdPrefix={t31} />;
      $[58] = t31;
      $[59] = winner.drop.author;
      $[60] = t32;
    } else {
      t32 = $[60];
    }
    let t33;
    if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = <span className="tw-text-sm tw-text-iron-500">•</span>;
      $[61] = t33;
    } else {
      t33 = $[61];
    }
    let t34;
    if ($[62] !== winner.drop.created_at) {
      t34 = <WaveDropTime timestamp={winner.drop.created_at} />;
      $[62] = winner.drop.created_at;
      $[63] = t34;
    } else {
      t34 = $[63];
    }
    let t35;
    if ($[64] !== t29 || $[65] !== t30 || $[66] !== t32 || $[67] !== t34) {
      t35 = <div className="tw-flex tw-items-center"><div className="-tw-mt-0.5 tw-flex tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-gap-y-1">{t29}{t30}{t32}{t33}{t34}</div></div>;
      $[64] = t29;
      $[65] = t30;
      $[66] = t32;
      $[67] = t34;
      $[68] = t35;
    } else {
      t35 = $[68];
    }
    let t36;
    if ($[69] !== t28 || $[70] !== t35) {
      t36 = <div className="tw-flex tw-gap-x-2">{t28}{t35}</div>;
      $[69] = t28;
      $[70] = t35;
      $[71] = t36;
    } else {
      t36 = $[71];
    }
    let t37;
    if ($[72] !== extendedDrop || $[73] !== hasTouchScreen) {
      t37 = !hasTouchScreen && <div className="tw-flex tw-flex-shrink-0 tw-items-center"><div className="tw-h-8"><WaveDropActionsOpen drop={extendedDrop} /></div></div>;
      $[72] = extendedDrop;
      $[73] = hasTouchScreen;
      $[74] = t37;
    } else {
      t37 = $[74];
    }
    if ($[75] !== t36 || $[76] !== t37) {
      t17 = <div className="tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-p-4 tw-pb-3"><div className="tw-flex tw-items-center tw-justify-between tw-gap-4">{t36}{t37}</div></div>;
      $[75] = t36;
      $[76] = t37;
      $[77] = t17;
    } else {
      t17 = $[77];
    }
    const t38 = artworkMedia?.mime_type;
    let t39;
    if ($[78] !== t38 || $[79] !== winner.drop.id) {
      t39 = <MediaTypeBadge mimeType={t38} dropId={winner.drop.id} size="sm" />;
      $[78] = t38;
      $[79] = winner.drop.id;
      $[80] = t39;
    } else {
      t39 = $[80];
    }
    let t40;
    if ($[81] !== title) {
      t40 = <h3 className="tw-mb-0 tw-text-base tw-font-semibold tw-text-iron-100">{title}</h3>;
      $[81] = title;
      $[82] = t40;
    } else {
      t40 = $[82];
    }
    let t41;
    if ($[83] !== t39 || $[84] !== t40) {
      t41 = <div className="tw-flex tw-items-center tw-gap-x-2">{t39}{t40}</div>;
      $[83] = t39;
      $[84] = t40;
      $[85] = t41;
    } else {
      t41 = $[85];
    }
    let t42;
    if ($[86] !== description) {
      t42 = <p className="tw-mb-0 tw-line-clamp-2 tw-text-sm tw-text-iron-400">{description}</p>;
      $[86] = description;
      $[87] = t42;
    } else {
      t42 = $[87];
    }
    if ($[88] !== t41 || $[89] !== t42) {
      t18 = <div className="tw-px-4 tw-pb-4 tw-pt-4"><div className="tw-space-y-1">{t41}{t42}</div></div>;
      $[88] = t41;
      $[89] = t42;
      $[90] = t18;
    } else {
      t18 = $[90];
    }
    if ($[91] !== winner.drop) {
      t19 = <WaveWinnerIdentity drop={winner.drop} variant="full" cardVariant="chat" className="tw-px-4 tw-pb-4" />;
      $[91] = winner.drop;
      $[92] = t19;
    } else {
      t19 = $[92];
    }
    if ($[93] !== artworkMedia) {
      t20 = artworkMedia && <div className="tw-flex tw-h-96 tw-justify-center tw-bg-iron-950"><DropListItemContentMedia media_mime_type={artworkMedia.mime_type} media_url={artworkMedia.url} isCompetitionDrop={true} /></div>;
      $[93] = artworkMedia;
      $[94] = t20;
    } else {
      t20 = $[94];
    }
    t13 = "tw-mt-4 tw-space-y-4 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-p-4";
    if ($[95] !== winner.drop) {
      t14 = <MemeDropTraits drop={winner.drop} />;
      $[95] = winner.drop;
      $[96] = t14;
    } else {
      t14 = $[96];
    }
    t11 = "tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-pt-1";
    const t43 = `tw-font-medium ${totalVoteClass}`;
    let t44;
    if ($[97] !== rating) {
      t44 = formatNumberWithCommas(rating);
      $[97] = rating;
      $[98] = t44;
    } else {
      t44 = $[98];
    }
    let t45;
    if ($[99] !== t43 || $[100] !== t44) {
      t45 = <span className={t43}>{t44}</span>;
      $[99] = t43;
      $[100] = t44;
      $[101] = t45;
    } else {
      t45 = $[101];
    }
    let t46;
    if ($[102] !== creditType) {
      t46 = <span className="tw-font-normal tw-text-iron-400">{creditType} {WAVE_VOTE_STATS_LABELS.TOTAL}</span>;
      $[102] = creditType;
      $[103] = t46;
    } else {
      t46 = $[103];
    }
    if ($[104] !== t45 || $[105] !== t46) {
      t12 = <div className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-leading-5">{t45}{t46}</div>;
      $[104] = t45;
      $[105] = t46;
      $[106] = t12;
    } else {
      t12 = $[106];
    }
    t10 = "tw-flex tw-items-center tw-gap-2 tw-text-sm tw-leading-5";
    t8 = "tw-flex tw-items-center -tw-space-x-2";
    t9 = topVoters.map(_temp6);
    $[11] = artworkMedia;
    $[12] = description;
    $[13] = hasTouchScreen;
    $[14] = onDropClick;
    $[15] = rating;
    $[16] = title;
    $[17] = touchHandlers;
    $[18] = wave.voting.credit_type;
    $[19] = winner;
    $[20] = creditType;
    $[21] = extendedDrop;
    $[22] = hasUserVoted;
    $[23] = isUserVoteNegative;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
    $[30] = t16;
    $[31] = t17;
    $[32] = t18;
    $[33] = t19;
    $[34] = t20;
    $[35] = t21;
    $[36] = t22;
    $[37] = t23;
    $[38] = t24;
    $[39] = t8;
    $[40] = t9;
    $[41] = userVote;
    $[42] = userVoteClass;
  } else {
    creditType = $[20];
    extendedDrop = $[21];
    hasUserVoted = $[22];
    isUserVoteNegative = $[23];
    t10 = $[24];
    t11 = $[25];
    t12 = $[26];
    t13 = $[27];
    t14 = $[28];
    t15 = $[29];
    t16 = $[30];
    t17 = $[31];
    t18 = $[32];
    t19 = $[33];
    t20 = $[34];
    t21 = $[35];
    t22 = $[36];
    t23 = $[37];
    t24 = $[38];
    t8 = $[39];
    t9 = $[40];
    userVote = $[41];
    userVoteClass = $[42];
  }
  let t25;
  if ($[107] !== t8 || $[108] !== t9) {
    t25 = <div className={t8}>{t9}</div>;
    $[107] = t8;
    $[108] = t9;
    $[109] = t25;
  } else {
    t25 = $[109];
  }
  let t26;
  if ($[110] !== ratersCount) {
    t26 = formatNumberWithCommas(ratersCount);
    $[110] = ratersCount;
    $[111] = t26;
  } else {
    t26 = $[111];
  }
  const t27 = ratersCount === 1 ? "voter" : "voters";
  let t28;
  if ($[112] !== t27) {
    t28 = <span className="tw-font-normal tw-text-iron-400">{t27}</span>;
    $[112] = t27;
    $[113] = t28;
  } else {
    t28 = $[113];
  }
  let t29;
  if ($[114] !== t26 || $[115] !== t28) {
    t29 = <span className="tw-font-medium tw-text-iron-50">{t26}{" "}{t28}</span>;
    $[114] = t26;
    $[115] = t28;
    $[116] = t29;
  } else {
    t29 = $[116];
  }
  let t30;
  if ($[117] !== t10 || $[118] !== t25 || $[119] !== t29) {
    t30 = <div className={t10}>{t25}{t29}</div>;
    $[117] = t10;
    $[118] = t25;
    $[119] = t29;
    $[120] = t30;
  } else {
    t30 = $[120];
  }
  let t31;
  if ($[121] !== creditType || $[122] !== hasUserVoted || $[123] !== isUserVoteNegative || $[124] !== userVote || $[125] !== userVoteClass) {
    t31 = hasUserVoted && <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-leading-5"><div className="tw-flex tw-items-baseline tw-gap-x-1"><span className="tw-font-normal tw-text-iron-400">{WAVE_VOTE_STATS_LABELS.YOUR_VOTES}:</span><span className={`tw-font-medium ${userVoteClass}`}>{isUserVoteNegative && "-"}{formatNumberWithCommas(Math.abs(userVote))}{" "}<span className="tw-font-normal tw-text-iron-400">{creditType}</span></span></div></div>;
    $[121] = creditType;
    $[122] = hasUserVoted;
    $[123] = isUserVoteNegative;
    $[124] = userVote;
    $[125] = userVoteClass;
    $[126] = t31;
  } else {
    t31 = $[126];
  }
  let t32;
  if ($[127] !== t11 || $[128] !== t12 || $[129] !== t30 || $[130] !== t31) {
    t32 = <div className={t11}>{t12}{t30}{t31}</div>;
    $[127] = t11;
    $[128] = t12;
    $[129] = t30;
    $[130] = t31;
    $[131] = t32;
  } else {
    t32 = $[131];
  }
  let t33;
  if ($[132] !== t13 || $[133] !== t14 || $[134] !== t32) {
    t33 = <div className={t13}>{t14}{t32}</div>;
    $[132] = t13;
    $[133] = t14;
    $[134] = t32;
    $[135] = t33;
  } else {
    t33 = $[135];
  }
  let t34;
  if ($[136] !== extendedDrop || $[137] !== handleMobileMenuClose || $[138] !== handleMobileMenuOpenChange || $[139] !== hasTouchScreen || $[140] !== isActive || $[141] !== setIsActive) {
    t34 = hasTouchScreen && createPortal(<CommonDropdownItemsMobileWrapper isOpen={isActive} setOpen={handleMobileMenuOpenChange}><div onClickCapture={handleMenuClickCapture} className="tw-grid tw-grid-cols-1 tw-gap-y-2"><WaveDropMobileMenuOpen drop={extendedDrop} onOpenChange={handleMobileMenuClose} /><WaveDropMobileMenuCopyLink drop={extendedDrop} onCopy={() => setIsActive(false)} /></div></CommonDropdownItemsMobileWrapper>, document.body);
    $[136] = extendedDrop;
    $[137] = handleMobileMenuClose;
    $[138] = handleMobileMenuOpenChange;
    $[139] = hasTouchScreen;
    $[140] = isActive;
    $[141] = setIsActive;
    $[142] = t34;
  } else {
    t34 = $[142];
  }
  let t35;
  if ($[143] !== t15 || $[144] !== t16 || $[145] !== t17 || $[146] !== t18 || $[147] !== t19 || $[148] !== t20 || $[149] !== t33 || $[150] !== t34) {
    t35 = <div className={t15} {...t16}>{t17}{t18}{t19}{t20}{t33}{t34}</div>;
    $[143] = t15;
    $[144] = t16;
    $[145] = t17;
    $[146] = t18;
    $[147] = t19;
    $[148] = t20;
    $[149] = t33;
    $[150] = t34;
    $[151] = t35;
  } else {
    t35 = $[151];
  }
  let t36;
  if ($[152] !== t21 || $[153] !== t35) {
    t36 = <div className={t21}>{t35}</div>;
    $[152] = t21;
    $[153] = t35;
    $[154] = t36;
  } else {
    t36 = $[154];
  }
  let t37;
  if ($[155] !== t22 || $[156] !== t23 || $[157] !== t24 || $[158] !== t36) {
    t37 = <div onClickCapture={t22} onClick={t23} className={t24}>{t36}</div>;
    $[155] = t22;
    $[156] = t23;
    $[157] = t24;
    $[158] = t36;
    $[159] = t37;
  } else {
    t37 = $[159];
  }
  return t37;
};
function _temp(m) {
  return m.data_key === "title";
}
function _temp2(m_0) {
  return m_0.data_key === "description";
}
function _temp3(e) {
  return e.stopPropagation();
}
function _temp4(e_0) {
  return e_0.stopPropagation();
}
function _temp5(e_1) {
  return e_1.stopPropagation();
}
function _temp6(voter) {
  return <React.Fragment key={voter.profile.handle}><Link href={`/${voter.profile.handle ?? voter.profile.id}`} onClick={_temp5} scroll={false} className="tw-transition-transform desktop-hover:hover:tw-translate-y-[-2px]" data-tooltip-id={`voter-${voter.profile.handle ?? voter.profile.primary_address}-${voter.rating}`}>{voter.profile.pfp ? <Image className="tw-h-6 tw-w-6 tw-rounded-md tw-border-2 tw-border-solid tw-border-[#111] tw-bg-iron-800 tw-object-contain" src={getScaledImageUri(voter.profile.pfp, ImageScale.W_AUTO_H_50)} alt={`${voter.profile.handle ?? voter.profile.id}'s profile picture`} width={24} height={24} /> : <div className="tw-h-6 tw-w-6 tw-rounded-md tw-border-2 tw-border-solid tw-border-[#111] tw-bg-iron-800" />}</Link><Tooltip id={`voter-${voter.profile.handle ?? voter.profile.primary_address}-${voter.rating}`} place="top" offset={8} opacity={1} positionStrategy="fixed" style={TOOLTIP_STYLES}>{voter.profile.handle} -{" "}{formatNumberWithCommas(voter.rating)}</Tooltip></React.Fragment>;
}